import moment from 'moment'
import * as React from 'react'
import {
  cancelledTripsStatusesDashboard,
  completedTripStatusesDashboard,
  scheduledTripStatuses,
  unsassignedTripStatusesDashboard,
  willCallTripStatuses,
  issuesTripStatuses
} from 'src/constants/trip-statuses'
import { nextRouteIs } from 'src/lib/route/location'
import { ICountTripStatusFilter, ICountTripStatusResp, ITripFilters } from 'src/types'
import UIMessages from 'src/utils/ui-messages'

const INTERVAL = 60000 // 60 seconds or 1 minute interval
const STUCK = ['stuck']
const ACTIVE_DASHBOARD = ['activeDashboard']
interface ITripsDashboardFilter {
  tripFilters?: ITripFilters
  currentOrganizationId: string
  tripStatusCount: ICountTripStatusResp[]
  updateTripFilters: (filters: ITripFilters) => void
  getTripStatusesCount: (params: ICountTripStatusFilter) => void
}
const TripsDashboardFilter = (props: ITripsDashboardFilter) => {
  const [stuckTripsCount, setStuckTripsCount] = React.useState(0)
  const [activeCount, setActiveCount] = React.useState(0)
  const [selectedStatsus, setSelectedStatus] = React.useState('')
  React.useEffect(() => {
    const stuckCount = props.tripStatusCount.find(item => item.last_status === 'INCOMPLETE')
    const activeTripCount = props.tripStatusCount.find(item => item.last_status === 'ACTIVE')
    setActiveCount(activeTripCount ? activeTripCount.count : 0)
    setStuckTripsCount(stuckCount ? stuckCount.count : 0)
  }, [props.updateTripFilters, props.currentOrganizationId, props.tripFilters, props.tripStatusCount])
  React.useEffect(() => {
    if (!props.currentOrganizationId) {
      return
    }
    const interval = setInterval(() => {
      getTripstatusCount()
    }, INTERVAL)
    return () => clearInterval(interval)
  }, [props.currentOrganizationId, props.tripFilters])
  React.useEffect(() => {
    if (!props.currentOrganizationId) {
      return
    }
    getTripstatusCount()
  }, [props.currentOrganizationId, props.tripFilters])

  const getTripstatusCount = () => {
    const serviceId = props.tripFilters && props.tripFilters.serviceId ? props.tripFilters.serviceId : ''

    props.getTripStatusesCount({
      orgId: props.currentOrganizationId,
      timeStart: moment()
        .startOf('day')
        .toISOString(),
      timeEnd: moment()
        .endOf('day')
        .toISOString(),
      serviceId
    })
  }
  const updateFilters = (selectedStatus: string, status?: string[]) => {
    const isInSchedulerRoute = nextRouteIs('/scheduler')
    const defaultDateRangeFilter = {
      timeStart: moment()
        .subtract(1, 'days')
        .startOf('day')
        .toISOString(),
      timeEnd: moment()
        .endOf('day')
        .toISOString()
    }
    // return defaultDateRangeFilter if not in /scheduler page else return existing time filter
    const timeRangeFilters = () => {
      if (!isInSchedulerRoute && props.tripFilters && props.tripFilters.timeStart) {
        return defaultDateRangeFilter
      }
      return {}
    }
    if (selectedStatus === 'Requests' || selectedStatus === 'Scheduled') {
      props.updateTripFilters({
        ...props.tripFilters,
        timeField: !isInSchedulerRoute ? 'pickup' : 'createdAndPickup',
        ...timeRangeFilters(),
        sortBy: ['scheduledPickupTime-asc'],
        getDashBoardData: true,
        status
      })
    } else {
      props.updateTripFilters({
        ...props.tripFilters,
        timeField: !isInSchedulerRoute ? 'lastStatusAt' : 'createdAndPickup',
        ...timeRangeFilters(),
        sortBy: ['lastStatus-desc'],
        getDashBoardData: true,
        status
      })
    }
    setSelectedStatus(selectedStatus)
  }
  const clearFilters = () => {
    const schedulerRouteFilters = () => {
      if (!nextRouteIs('/scheduler')) {
        return {
          timeStart: undefined,
          timeEnd: undefined
        }
      }
      return {}
    }
    props.updateTripFilters({
      timeField: 'createdAndPickup',
      ...schedulerRouteFilters(),
      status: undefined,
      getDashBoardData: undefined,
      serviceId: undefined
    })
    setSelectedStatus(UIMessages.common.all)
  }
  const countTripStatus = (tripStatusArr: ICountTripStatusResp[], statusesToCount: string[]): number => {
    let count = 0
    tripStatusArr
      .filter(item => statusesToCount.includes(item.last_status))
      .forEach(item => {
        count = count + item.count
      })
    return count
  }

  return (
    <div className='tableInner'>
      <div className='tableBody'>
        {/* Flexbox for the body */}
        <div
          className={`statusItem ${selectedStatsus === UIMessages.common.all ? 'font-green' : 'font-black'}`}
          onClick={clearFilters}
          style={{ justifyContent: 'end' }}
        >
          {UIMessages.common.all}
        </div>

        <div
          className={`statusItem ${selectedStatsus === UIMessages.common.issues ? 'font-green' : 'font-black'}`}
          onClick={() => updateFilters(UIMessages.common.issues, issuesTripStatuses)}
        >
          <div>{countTripStatus(props.tripStatusCount, issuesTripStatuses)}</div>
          {UIMessages.common.issues}
        </div>

        <div
          className={`statusItem ${selectedStatsus === UIMessages.common.unassigned ? 'font-green' : 'font-black'}`}
          onClick={() => updateFilters(UIMessages.common.unassigned, unsassignedTripStatusesDashboard)}
        >
          <div>{countTripStatus(props.tripStatusCount, unsassignedTripStatusesDashboard)}</div>
          {UIMessages.common.unassigned}
        </div>

        <div
          className={`statusItem ${selectedStatsus === UIMessages.common.willCall ? 'font-green' : 'font-black'}`}
          onClick={() => updateFilters(UIMessages.common.willCall, willCallTripStatuses)}
        >
          <div>{countTripStatus(props.tripStatusCount, willCallTripStatuses)}</div>
          {UIMessages.common.willCallFilter}
        </div>

        <div
          className={`statusItem ${selectedStatsus === UIMessages.common.active ? 'font-green' : 'font-black'}`}
          onClick={() => updateFilters(UIMessages.common.active, ACTIVE_DASHBOARD)}
        >
          <div>{activeCount}</div>
          {UIMessages.common.active}
        </div>

        <div
          className={`statusItem ${
            selectedStatsus === UIMessages.trip.statuses.scheduled ? 'font-green' : 'font-black'
          }`}
          onClick={() => updateFilters(UIMessages.trip.statuses.scheduled, scheduledTripStatuses)}
        >
          <div>{countTripStatus(props.tripStatusCount, scheduledTripStatuses)}</div>
          {UIMessages.trip.statuses.scheduled}
        </div>

        <div
          className={`statusItem ${
            selectedStatsus === UIMessages.trip.statuses.canceled ? 'font-green' : 'font-black'
          }`}
          onClick={() => updateFilters(UIMessages.trip.statuses.canceled, cancelledTripsStatusesDashboard)}
        >
          <div>{countTripStatus(props.tripStatusCount, cancelledTripsStatusesDashboard)}</div>
          {UIMessages.trip.statuses.canceled}
        </div>

        <div
          className={`statusItem ${
            selectedStatsus === UIMessages.trip.statuses.completed ? 'font-green' : 'font-black'
          }`}
          onClick={() => updateFilters(UIMessages.trip.statuses.completed, completedTripStatusesDashboard)}
        >
          <div>{countTripStatus(props.tripStatusCount, completedTripStatusesDashboard)}</div>
          {UIMessages.trip.statuses.completed}
        </div>
      </div>
    </div>
  )
}

export default TripsDashboardFilter



// WEBPACK FOOTER //
// ./src/components/trips/TripsDashboardFilter.tsx